import type { ReactElement, RefObject } from 'react';

import { XIcon } from '@phosphor-icons/react';
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  startTransition,
  forwardRef,
  useImperativeHandle,
  useId,
} from 'react';
import { useSortBy, useStats } from 'react-instantsearch';
import { CSSTransition } from 'react-transition-group';

import { Facet } from './Facet';
import { FILTER_MENU_CONFIG } from './filterMenuConstants';
import { S } from './FilterMenuStyles';
import { SortingDropdown } from './SortingDropdown';
import { SortingOption } from './SortingOption';
import { Skeleton } from '~/components/shared/common/SkeletonPlaceholder';
import type { CTAVariantProps } from '~/components/shared/core/button/CTA';
import PhosphorSlidersVerticalIcon from '~/components/shared/core/icons/PhosphorSlidersVerticalIcon';
import { NUMBER_OF_RESULTS_VARIABLE } from '~/utilities/constants/amplienceVariables';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { useMediaQuery } from '~/utilities/dom';
import { SiteType } from '~/utilities/graphql/codegen';
import { useAlgoliaFacets } from '~/utilities/hooks/search/useAlgoliaFacets';
import { useAlgoliaLoadingState } from '~/utilities/hooks/search/useAlgoliaLoadingState';
import { useAlgoliaSortingOptions } from '~/utilities/hooks/search/useAlgoliaSortingOptions';
import { useAppliedFacets } from '~/utilities/hooks/search/useAppliedFacets';
import { useClearAllAppliedFacets } from '~/utilities/hooks/search/useClearAllAppliedFacets';

type FilterMenuProps = {
  filterBarRef: RefObject<HTMLDivElement | null>;
};

export type FilterMenuRef = {
  openFiltersOverlay: () => void;
};

const ClearAllCTAPropsMap: Record<SiteType, CTAVariantProps> = {
  [SiteType.Aaf]: { variant: 'outline', colorway: 'dark' },
  [SiteType.Employeeshop]: { variant: 'outline', colorway: 'dark' },
  [SiteType.Gstar]: { variant: 'outline', colorway: 'dark' },
  [SiteType.Outlet]: { variant: 'outline', colorway: 'light' },
  [SiteType.Wahts]: { variant: 'outline', colorway: 'dark' },
};

const ApplyFiltersCTAPropsMap: Record<SiteType, CTAVariantProps> = {
  [SiteType.Aaf]: { variant: 'primary' },
  [SiteType.Employeeshop]: { variant: 'primary' },
  [SiteType.Gstar]: { variant: 'primary' },
  [SiteType.Outlet]: { variant: 'solid', colorway: 'light' },
  [SiteType.Wahts]: { variant: 'primary' },
};

export const FilterMenu = forwardRef<FilterMenuRef, FilterMenuProps>(
  ({ filterBarRef }, forwardedRef): ReactElement => {
    const { isLoading, isFirstLoading } = useAlgoliaLoadingState();
    const { systemTexts } = useProductListContext();
    const { accessibilityLabels } = useStaticContext();
    const { sortingOptions, defaultSortingSuffix, getIndexName } = useAlgoliaSortingOptions();
    const { numberOfAppliedFacets } = useAppliedFacets();
    const clearAllAppliedFacets = useClearAllAppliedFacets();

    const { refine: refineSortBy } = useSortBy({ items: sortingOptions });
    const { nbHits } = useStats();
    const facets = useAlgoliaFacets();
    const { isMobile } = useMediaQueryContext();
    const { siteType } = useAppContext();

    const [isOpen, setIsOpen] = useState(false);
    const [sticky, setSticky] = useState(false);
    const [expandedItem, setExpandedItem] = useState<string | null>(null);
    const [resultsAnnouncement, setResultsAnnouncement] = useState('');

    const contentRef = useRef<HTMLDivElement | null>(null);
    const focusedElementBeforeDialogRef = useRef<HTMLElement | null>(null);
    const focusRestorationFrameRef = useRef<number | null>(null);
    const hasSettledResultsRef = useRef(false);
    const previousSettledNbHitsRef = useRef(nbHits);
    const previousAppliedFacetsCountRef = useRef(numberOfAppliedFacets);
    const filterDialogId = useId();
    const filterDialogTitleId = useId();
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const transitionTimeout = prefersReducedMotion ? 0 : FILTER_MENU_CONFIG.ANIMATION.DURATION_MS;

    const filterDialogTitle =
      systemTexts?.filterAndSort?.pluralFilterBtnText ||
      systemTexts?.filterAndSort?.filterAndSortBtnText ||
      'Filters';
    const resultsNoun = nbHits === 1 ? 'result' : 'results';
    const fallbackResultsLabel = `Done, show ${nbHits.toString()} ${resultsNoun}`;
    const doneResultsLabel = systemTexts?.filterAndSort?.doneResultsText
      ? systemTexts.filterAndSort.doneResultsText.replace(
          NUMBER_OF_RESULTS_VARIABLE,
          nbHits.toString()
        )
      : fallbackResultsLabel;

    // "Clear all" restores the category's own default sorting, which is not necessarily relevance
    const defaultSortOption = getIndexName(defaultSortingSuffix);

    useEffect(() => {
      if (isLoading || isFirstLoading) {
        return;
      }

      if (!hasSettledResultsRef.current) {
        hasSettledResultsRef.current = true;
        previousSettledNbHitsRef.current = nbHits;
        previousAppliedFacetsCountRef.current = numberOfAppliedFacets;

        return;
      }

      if (
        previousSettledNbHitsRef.current === nbHits &&
        previousAppliedFacetsCountRef.current === numberOfAppliedFacets
      ) {
        return;
      }

      previousSettledNbHitsRef.current = nbHits;
      previousAppliedFacetsCountRef.current = numberOfAppliedFacets;
      setResultsAnnouncement(doneResultsLabel);
    }, [doneResultsLabel, isFirstLoading, isLoading, nbHits, numberOfAppliedFacets]);

    useEffect(() => {
      let animationFrameId = 0;

      const updateSticky = () => {
        startTransition(() => {
          const cssTopOffset = Math.round(filterBarRef.current?.getBoundingClientRect().top || 0);
          const topStr = filterBarRef.current
            ? window.getComputedStyle(filterBarRef.current).top
            : '0';
          const computedStickyTopOffset = Number.parseInt(topStr || '0', 10);
          const isStuck = cssTopOffset <= computedStickyTopOffset;

          setSticky(isStuck);
        });
      };

      const onScroll = () => {
        animationFrameId = requestAnimationFrame(updateSticky);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      return () => {
        window.removeEventListener('scroll', onScroll);
        cancelAnimationFrame(animationFrameId);
      };
    }, [filterBarRef]);

    const resetViewOptions = useCallback(() => {
      refineSortBy(defaultSortOption);
    }, [refineSortBy, defaultSortOption]);

    const handleItemToggle = useCallback((itemKey: string) => {
      setExpandedItem(prev => (prev === itemKey ? null : itemKey));
    }, []);

    const openFiltersOverlay = useCallback((): void => {
      focusedElementBeforeDialogRef.current = document.activeElement as HTMLElement;
      setIsOpen(true);
    }, []);

    const closeFiltersOverlay = useCallback((): void => {
      setIsOpen(false);
    }, []);

    const restoreFocus = useCallback((): void => {
      if (focusRestorationFrameRef.current !== null) {
        cancelAnimationFrame(focusRestorationFrameRef.current);
      }

      focusRestorationFrameRef.current = requestAnimationFrame(() => {
        focusedElementBeforeDialogRef.current?.focus();
        focusedElementBeforeDialogRef.current = null;
        focusRestorationFrameRef.current = null;
      });
    }, []);

    useEffect(
      () => () => {
        if (focusRestorationFrameRef.current !== null) {
          cancelAnimationFrame(focusRestorationFrameRef.current);
        }
      },
      []
    );

    useImperativeHandle(
      forwardedRef,
      () => ({
        openFiltersOverlay,
      }),
      [openFiltersOverlay]
    );

    const filterBtnLabel = useCallback(() => {
      if (isFirstLoading) {
        return ' ';
      }

      if (isMobile) {
        return systemTexts?.filterAndSort?.filterAndSortBtnText || '';
      }

      return systemTexts?.filterAndSort?.singularFilterBtnText || '';
    }, [isFirstLoading, isMobile, systemTexts?.filterAndSort]);

    return (
      <S.ViewOptionsContainer>
        <Skeleton when={isFirstLoading} width="100px" height="26px">
          <S.FilterButtonWrapper data-stuck={sticky}>
            <S.FilterButton
              aria-controls={filterDialogId}
              aria-expanded={isOpen}
              aria-haspopup="dialog"
              disabled={isFirstLoading}
              data-testid="filter-menu"
              onClick={openFiltersOverlay}
            >
              {filterBtnLabel()}
              <PhosphorSlidersVerticalIcon />
            </S.FilterButton>
          </S.FilterButtonWrapper>

          <CSSTransition
            in={isOpen}
            timeout={transitionTimeout}
            nodeRef={contentRef}
            classNames="filter-dialog"
            mountOnEnter
            unmountOnExit
            onExited={restoreFocus}
          >
            <S.Dialog onDismiss={closeFiltersOverlay} allowPinchZoom>
              <S.DialogContent
                ref={contentRef}
                id={filterDialogId}
                aria-labelledby={filterDialogTitleId}
                data-testid="filter-menu-dialog"
              >
                <S.DialogHeader>
                  <S.HeaderLabel id={filterDialogTitleId}>{filterDialogTitle}</S.HeaderLabel>
                  <S.CloseBtn
                    type="button"
                    aria-label={
                      accessibilityLabels?.closeButton || systemTexts?.filterAndSort?.filtersDone
                    }
                    data-testid="filter-menu-close"
                    onClick={closeFiltersOverlay}
                  >
                    <XIcon
                      aria-hidden="true"
                      weight="bold"
                      size={FILTER_MENU_CONFIG.DIMENSIONS.ICON_SIZE}
                    />
                  </S.CloseBtn>
                </S.DialogHeader>

                <S.InnerWrapper>
                  <S.FiltersAndViewOptions>
                    {isMobile && (
                      <SortingOption
                        isExpanded={expandedItem === 'sort'}
                        onToggle={() => handleItemToggle('sort')}
                      />
                    )}
                    {facets.map((facet, index) => {
                      const uniqueKey = `${facet.attribute}-${index}`;

                      return (
                        <Facet
                          key={uniqueKey}
                          {...facet}
                          isExpanded={expandedItem === uniqueKey}
                          onToggle={() => handleItemToggle(uniqueKey)}
                        />
                      );
                    })}
                  </S.FiltersAndViewOptions>
                </S.InnerWrapper>

                <S.DialogFooter>
                  {numberOfAppliedFacets > 0 && (
                    <S.Button
                      label={systemTexts?.filterAndSort?.clearAllLabel || ''}
                      {...ClearAllCTAPropsMap[siteType]}
                      onClick={() => {
                        clearAllAppliedFacets();
                        resetViewOptions();
                      }}
                    />
                  )}
                  <S.Button
                    data-testid="filter-menu-apply"
                    label={doneResultsLabel}
                    {...ApplyFiltersCTAPropsMap[siteType]}
                    onClick={closeFiltersOverlay}
                  />
                </S.DialogFooter>
                <S.ResultsStatus
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  data-testid="filter-results-status"
                >
                  {resultsAnnouncement}
                </S.ResultsStatus>
              </S.DialogContent>
            </S.Dialog>
          </CSSTransition>
        </Skeleton>
        {!isOpen && (
          <S.ResultsStatus
            role="status"
            aria-live="polite"
            aria-atomic="true"
            data-testid="filter-results-status"
          >
            {resultsAnnouncement}
          </S.ResultsStatus>
        )}
        {!isMobile && <SortingDropdown isFirstLoading={isFirstLoading} />}
      </S.ViewOptionsContainer>
    );
  }
);

FilterMenu.displayName = 'FilterMenu';
